import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminShared.css";
import { API_BASE_URL } from "../config";

export default function ViewAdmins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // 🔐 STABILIZE SESSION DATA - Force fresh read on mount
  const session = useMemo(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("adminInfo") || "null");
      if (!stored) return null;
      return {
        token: stored.token,
        role: stored.user?.role || stored.admin?.role, // Support both formats
        permissions: stored.user?.permissions || stored.admin?.permissions || []
      };
    } catch (e) { return null; }
  }, []);

  const token = session?.token;
  const userRole = session?.role;

  const fetchAdmins = useCallback(async () => {
    if (!token) {
      navigate("/admin");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${API_BASE_URL}/admin/all`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });

      if (!res.ok) {
        if (res.status === 403) throw new Error("ACCESS_DENIED");
        if (res.status === 401) throw new Error("UNAUTHORIZED");
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `Operational Error: ${res.status}`);
      }

      const data = await res.json();
      if (Array.isArray(data)) {
        setAdmins(data);
      } else {
        throw new Error("Invalid registry format received.");
      }
    } catch (err) {
      console.error("Registry Scan Failure:", err);
      if (err.message === "ACCESS_DENIED") {
        setError("Restricted Access: SuperAdmin authorization required.");
      } else if (err.message === "UNAUTHORIZED") {
        navigate("/admin");
      } else {
        setError(err.message || "Failed to synchronize with security node.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    // Immediate security check
    if (!token) {
      navigate("/admin");
      return;
    }

    if (userRole !== "superAdmin") {
      setError("Operational Restriction: SuperAdmin credentials required to view registry.");
      return;
    }

    fetchAdmins();
  }, [fetchAdmins, userRole, token, navigate]);

  const deleteAdmin = async (id) => {
    if (!window.confirm("Purge this operator's access credentials?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Purge failed");
      setAdmins(prev => prev.filter((a) => a._id !== id));
    } catch (err) { alert("Security Purge Failed: Internal Error"); }
  };

  if (loading) return (
    <div className="admin-list-container">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh', gap: '20px' }}>
        <div className="loading-spinner"></div>
        <p style={{ fontWeight: 700, color: '#475569' }}>⏳ Scanning Security Access Matrix...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="admin-list-container">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh', textAlign: 'center', padding: '0 20px' }}>
        <div style={{ fontSize: '3rem', marginBottom: '20px' }}>⚠️</div>
        <h2 style={{ color: '#0f172a', marginBottom: '10px' }}>Security Protocol Break</h2>
        <p style={{ color: '#64748b', maxWidth: '400px', marginBottom: '30px' }}>{error}</p>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn-primary-elite" onClick={fetchAdmins}>Retry Scan</button>
          <button className="btn-update" onClick={() => navigate("/admin/dashboard")}>Back to Dashboard</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="admin-list-container">
      <header className="admin-list-header">
        <div className="header-left-group">
          <img src="/logo.png" alt="FlyTutor" className="admin-header-logo" />
          <h1 className="admin-header-title">Security Permissions</h1>
        </div>
        <button className="module-badge" style={{ cursor: 'pointer', background: '#475569', border: 'none' }} onClick={() => navigate("/admin/add-admin")}>
          + Register Operator
        </button>
      </header>

      <div className="admin-list-content">
        <div className="admin-info-bar">
          <div className="admin-user-tag">
            <div className="dot" style={{ background: '#475569' }}></div>
            Operator: Security Protocol Node
          </div>
          <div className="admin-status-badge" style={{ background: '#f8fafc', color: '#1e293b', fontSize: '0.75rem', fontWeight: 800 }}>
            {admins.length} ACTIVE OPERATORS
          </div>
        </div>

        <div className="admin-table-wrapper">
          <table className="elite-table">
            <thead>
              <tr>
                <th style={{ width: '250px' }}>Identity</th>
                <th style={{ width: '150px' }}>Access Role</th>
                <th>Permission Matrix</th>
                <th style={{ width: '120px' }}>Lifecycle</th>
                <th style={{ width: '150px' }}>Operations</th>
              </tr>
            </thead>
            <tbody>
              {admins.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>
                    No operator records found in the current buffer.
                  </td>
                </tr>
              ) : (
                admins.map((a) => (
                  <tr key={a._id}>
                    <td>
                      <div style={{ fontWeight: 800, color: '#0f172a' }}>{a.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>{a.email}</div>
                    </td>
                    <td>
                      <span className="status-tag" style={{
                        background: a.role === 'superAdmin' ? '#f0fdf4' : '#f1f5f9',
                        color: a.role === 'superAdmin' ? '#166534' : '#475569',
                        border: `1px solid ${a.role === 'superAdmin' ? '#dcfce7' : '#e2e8f0'}`
                      }}>
                        {a.role?.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxWidth: '500px' }}>
                        {a.permissions && a.permissions.length > 0 ? a.permissions.map(p => (
                          <span key={p} style={{
                            fontSize: '0.6rem',
                            padding: '4px 8px',
                            background: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            color: '#475569',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                          }}>
                            {p.replace(/_/g, ' ')}
                          </span>
                        )) : <span style={{ color: '#94a3b8', fontSize: '0.8rem', fontStyle: 'italic' }}>No persistent grants</span>}
                      </div>
                    </td>
                    <td>
                      <span className="status-tag status-contacted" style={{ background: '#ecfdf5', color: '#059669', fontSize: '0.65rem' }}>ACTIVE</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="action-btn-elite btn-update" onClick={() => navigate(`/admin/edit-admin/${a._id}`)}><span>✏️</span> Update</button>
                        <button className="action-btn-elite btn-delete" onClick={() => deleteAdmin(a._id)}><span>🗑️</span></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
